/**
 * 校验报告（可打印）
 *
 * 这份报告是给「签字的人」看的：财务初审、事后抽查、外部审计、税务自查都可能拿它当凭据。
 * 因此内容取舍上遵循两条：
 *   1. 只呈现**事实**（识别到什么、外部接口返回什么、比对结果是什么），不写结论性断言；
 *   2. 明确标注数据来源与不可用项，人工接管必须留下说明与操作人，避免「看起来都正常」的假象。
 */

import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Descriptions, Divider, Empty, Result, Space, Table, Tag, Typography } from 'antd';
import { PrinterOutlined, WarningOutlined } from '@ant-design/icons';
import {
  CHECK_STATUS_LABELS,
  CheckStatus,
  INVOICE_TYPE_LABELS,
  INVOICE_VALIDITY_LABELS,
  InvoiceValidity,
  PERMISSION_BY_CODE,
  PermissionCode,
  PROVIDER_MODE_LABELS,
  ProviderMode,
  RISK_ITEM_LABELS,
  RiskLevel,
  type CheckReport as CheckReportType,
  type InvoiceDetail,
  type OrchestrationStep,
  type RiskItem,
} from '@ilc/shared';
import { invoiceApi } from '@/api/endpoints';
import { mockCheckReport, mockInvoiceDetail } from '@/mocks';
import { useResource } from '@/hooks/useResource';
import { useAuthStore } from '@/stores/auth';
import { PageHeader } from '@/components/PageHeader';
import { ScopeMatchTag } from '@/components/ScopeMatchTag';
import { PageState, FallbackNotice, ErrorState } from '@/components/PageState';
import { RiskTag } from '@/components/RiskTag';
import { CheckStatusTag } from '@/components/CheckStatusTag';
import { ProviderModeTag, isUntrustedMode } from '@/components/ProviderModeTag';
import { MaskedField } from '@/components/MaskedField';
import { Money } from '@/components/Money';

function SectionTitle({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <Divider orientation="left" plain style={{ margin: '12px 0 8px' }}>
      <Typography.Text strong>{children}</Typography.Text>
    </Divider>
  );
}

/** 编排步骤的中文名 */
const STEP_LABELS: Record<OrchestrationStep['step'], string> = {
  OCR: '发票识别',
  SUPPLIER_CHECK: '企查查主体校验',
  TAX_CHECK: '税务局验真',
  CONTRACT_MATCH: '合同比对',
  RISK_GRADING: '风险分级',
};

export default function CheckReport(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auth = useAuthStore((s) => s.auth);

  const report = useResource<CheckReportType>(() => invoiceApi.report(id ?? ''), {
    fallback: () => mockCheckReport(id ?? ''),
    deps: [id],
    enabled: Boolean(id),
  });

  /**
   * 报告接口只返回校验结果，人工接管记录挂在发票的 InvoiceCheck 上，
   * 因此这里额外取一次发票详情——报告页是审计凭据，缺了接管说明就不完整。
   * 取不到时页面照常展示报告，只是明确写出「未取得接管记录」。
   */
  const detail = useResource<InvoiceDetail>(() => invoiceApi.detail(id ?? ''), {
    fallback: () => mockInvoiceDetail(id ?? ''),
    deps: [id],
    enabled: Boolean(id),
  });

  if (!id) {
    return (
      <Result
        status="404"
        title="缺少发票 ID"
        extra={
          <Button type="primary" onClick={() => navigate('/business/invoices')}>
            返回列表
          </Button>
        }
      />
    );
  }

  const data = report.data;

  if (!data) {
    return (
      <div className="ilc-page">
        {report.loading ? (
          <PageState loading skeleton rows={10}>
            {null}
          </PageState>
        ) : (
          <>
            <ErrorState error={report.error} onRetry={report.reload} title="校验报告加载失败" />
            <Space style={{ marginTop: 12 }}>
              <Button onClick={() => navigate(`/business/invoices/${id}`)}>返回发票详情</Button>
              <Button type="primary" onClick={report.reload}>
                重新加载
              </Button>
            </Space>
          </>
        )}
      </div>
    );
  }

  const invoice = data.invoice;
  const riskItems = (data.riskItems ?? []) as RiskItem[];
  const ocrFields = data.ocr?.fields ?? {};
  const maskPermission = PermissionCode.INVOICE_READ;

  const orchestration = (data.orchestrationLog ?? []) as OrchestrationStep[];
  /** 未完成的校验项：外部接口不可用或执行失败 */
  const unavailableSteps = orchestration.filter(
    (step) => step.status === CheckStatus.UNAVAILABLE || step.status === CheckStatus.FAILED,
  );

  /**
   * 未走真实外部通道的步骤（沙箱模拟 / 通道未启用）。
   *
   * 与 `unavailableSteps` 是两回事：那些是"跑了但失败"，这些是
   * "跑完了、状态还显示成功，但结论不是真实校验得来的"。
   * 后者更隐蔽，**打印出来尤其危险**——一张看起来全绿的报告被当作
   * 审计凭据，而数据其实是沙箱编的。
   */
  const untrustedSteps = orchestration.filter((step) => isUntrustedMode(step.mode));

  /** 取某一步的运行模式；历史报告没有该字段时返回 undefined（组件显示「通道未记录」） */
  const stepModeOf = (step: string): ProviderMode | null | undefined =>
    orchestration.find((s) => s.step === step)?.mode;

  const manualOverride = detail.data?.check?.manualOverride ?? null;
  const hasUnavailable = data.checkUnavailable || unavailableSteps.length > 0;
  /**
   * 有不可用项、有未走真实通道的项、或已有接管记录，
   * 就单独出一节说明，避免「看起来都正常」。
   */
  const showOverrideSection = hasUnavailable || untrustedSteps.length > 0 || manualOverride !== null;

  const fieldRows = [
    { label: '发票代码', value: invoice.invoiceCode ?? '（数电票无代码）' },
    { label: '发票号码', value: invoice.invoiceNo },
    { label: '开票日期', value: invoice.invoiceDate ?? '-' },
    { label: '发票种类', value: INVOICE_TYPE_LABELS[invoice.invoiceType] ?? invoice.invoiceType },
    { label: '销方名称', value: invoice.sellerName ?? '-' },
    { label: '购方名称', value: invoice.buyerName ?? '-' },
  ];

  return (
    <div className="ilc-page">
      <PageHeader
        title="发票校验报告"
        description="本报告由平台自动汇总外部校验结果与人工操作记录生成，可作为审批附件与审计凭据。"
        extra={
          <Space className="ilc-no-print">
            <Button onClick={() => navigate(`/business/invoices/${id}`)}>返回发票详情</Button>
            <Button type="primary" icon={<PrinterOutlined />} onClick={() => window.print()}>
              打印 / 导出 PDF
            </Button>
          </Space>
        }
      />

      {report.isFallback ? (
        <div className="ilc-no-print">
          <FallbackNotice
            reason={report.fallbackReason}
            onRetry={report.reload}
            detail="当前报告内容为本地演示数据，不可作为真实凭据使用。"
          />
        </div>
      ) : null}

      <Card className="ilc-print-area">
        <div style={{ textAlign: 'center' }}>
          <Typography.Title level={4} style={{ marginBottom: 4 }}>
            发票校验报告
          </Typography.Title>
          <Typography.Text type="secondary">
            报告编号 <span className="ilc-mono">{data.reportNo}</span> · 内部单号{' '}
            <span className="ilc-mono">{invoice.bizNo}</span>
          </Typography.Text>
        </div>

        <SectionTitle>一、发票基本信息</SectionTitle>
        <Descriptions column={2} size="small" bordered>
          {fieldRows.map((row) => (
            <Descriptions.Item key={row.label} label={row.label}>
              <span className="ilc-mono">{row.value}</span>
            </Descriptions.Item>
          ))}
          <Descriptions.Item label="销方纳税人识别号">
            <MaskedField value={invoice.sellerTaxNo} kind="taxNo" permission={maskPermission} />
          </Descriptions.Item>
          <Descriptions.Item label="购方纳税人识别号">
            <MaskedField value={invoice.buyerTaxNo} kind="taxNo" permission={maskPermission} />
          </Descriptions.Item>
          <Descriptions.Item label="金额（不含税）">
            <Money cents={invoice.amountCents} symbol={false} />
          </Descriptions.Item>
          <Descriptions.Item label="税额">
            <Money cents={invoice.taxCents} symbol={false} />
          </Descriptions.Item>
          <Descriptions.Item label="价税合计">
            <Money cents={invoice.totalCents} strong withChinese />
          </Descriptions.Item>
          <Descriptions.Item label="发票有效性">
            <Tag color={invoice.validity === InvoiceValidity.VALID ? 'green' : 'red'}>
              {INVOICE_VALIDITY_LABELS[invoice.validity]}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="关联合同">
            {invoice.contractNo ? <span className="ilc-mono">{invoice.contractNo}</span> : '未关联'}
          </Descriptions.Item>
          <Descriptions.Item label="上传经办人">{invoice.handlerName ?? '-'}</Descriptions.Item>
        </Descriptions>

        <SectionTitle>二、OCR 识别结果</SectionTitle>
        {data.ocr ? (
          <Table
            size="small"
            rowKey="key"
            pagination={false}
            dataSource={Object.entries(ocrFields).map(([key, value]) => ({
              key,
              label: key,
              value: typeof value === 'string' ? value : JSON.stringify(value),
              confidence: data.ocr?.confidence[key] ?? null,
            }))}
            columns={[
              { title: '字段', dataIndex: 'label', width: 220 },
              {
                title: '识别值',
                dataIndex: 'value',
                render: (v: string) => <span className="ilc-mono">{v}</span>,
              },
              {
                title: '置信度',
                dataIndex: 'confidence',
                width: 100,
                render: (v: number | null) =>
                  v === null ? '—' : <span className={v < 80 ? 'ilc-num' : 'ilc-num'}>{v}%</span>,
              },
            ]}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="无 OCR 识别结果" />
        )}
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
          <Space size={6} wrap>
            <span>识别通道：</span>
            <ProviderModeTag
              mode={stepModeOf('OCR')}
              impl={data.ocr?.engine ?? null}
              itemName="OCR 识别"
            />
            <span>
              · 整体置信度 {data.ocr?.overallConfidence ?? '-'}% ·
              {data.ocr?.manuallyEdited ? ' 存在人工修正（修正明细见操作日志）' : ' 未发生人工修正'}
            </span>
          </Space>
        </Typography.Paragraph>

        <SectionTitle>三、企查查主体校验</SectionTitle>
        {data.supplier ? (
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="企业名称">{data.supplier.companyName ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="经营状态">
              <Tag color={data.supplier.status === '存续' || data.supplier.status === '在业' ? 'green' : 'red'}>
                {data.supplier.status ?? '未知'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="统一社会信用代码">
              <MaskedField value={data.supplier.creditCode} kind="taxNo" permission={maskPermission} />
            </Descriptions.Item>
            <Descriptions.Item label="法定代表人">{data.supplier.operName ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="注册资本">{data.supplier.registCapi ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="成立日期">{data.supplier.establishDate ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="经营范围匹配度">
              <ScopeMatchTag
                confidence={data.supplier.scopeMatchConfidence}
                matched={data.supplier.scopeMatched}
              />
            </Descriptions.Item>
            <Descriptions.Item label="校验状态">
              <CheckStatusTag status={data.supplier.checkStatus} itemName="企查查主体校验" />
            </Descriptions.Item>
            <Descriptions.Item label="经营范围原文" span={2}>
              <Typography.Paragraph style={{ fontSize: 12, marginBottom: 0 }}>
                {data.supplier.scope ?? '-'}
              </Typography.Paragraph>
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="无主体校验结果" />
        )}

        <SectionTitle>四、税务局验真</SectionTitle>
        {data.tax ? (
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="查验结论">
              <Tag color={data.tax.validity === InvoiceValidity.VALID ? 'green' : 'red'}>
                {INVOICE_VALIDITY_LABELS[data.tax.validity]}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="查验通道">
              <Space size={6}>
                <ProviderModeTag
                  mode={stepModeOf('TAX_CHECK')}
                  impl={data.tax.channel}
                  itemName="税务局验真"
                />
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="通道返回码">
              <span className="ilc-mono">{data.tax.channelCode ?? '-'}</span>
            </Descriptions.Item>
            <Descriptions.Item label="通道消息">{data.tax.channelMessage ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="作废/红冲标志">
              <span className="ilc-mono">{data.tax.invalidMark ?? '-'}</span>
            </Descriptions.Item>
            <Descriptions.Item label="校验状态">
              <CheckStatusTag status={data.tax.checkStatus} itemName="税务局验真" />
            </Descriptions.Item>
            <Descriptions.Item label="票面与税务记录不一致字段" span={2}>
              {data.tax.mismatchedFields?.length ? data.tax.mismatchedFields.join('、') : '无'}
            </Descriptions.Item>
            {data.tax.errorMessage ? (
              <Descriptions.Item label="异常信息" span={2}>
                <Typography.Text type="danger">{data.tax.errorMessage}</Typography.Text>
              </Descriptions.Item>
            ) : null}
          </Descriptions>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="无验真结果（可能尚未发起或外部接口不可用）" />
        )}

        <SectionTitle>五、合同比对</SectionTitle>
        {data.contract ? (
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="合同编号">
              <span className="ilc-mono">{data.contract.contractNo}</span>
            </Descriptions.Item>
            <Descriptions.Item label="是否超出合同额度">
              {data.contract.amountExceeded === null ? '未比对' : data.contract.amountExceeded ? '超出' : '未超出'}
            </Descriptions.Item>
            <Descriptions.Item label="品类是否一致">
              {data.contract.categoryMatched === null ? '未比对' : data.contract.categoryMatched ? '一致' : '不一致'}
            </Descriptions.Item>
            <Descriptions.Item label="结算阶段">
              {data.contract.stageMatched === null ? '未比对' : data.contract.stageMatched ? '匹配' : '不匹配'}
            </Descriptions.Item>
            <Descriptions.Item label="差异明细" span={2}>
              {data.contract.mismatchDetails?.length ? (
                <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                  {data.contract.mismatchDetails.map((d) => (
                    <li key={d}>{d}</li>
                  ))}
                </ul>
              ) : (
                '无'
              )}
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="未关联合同，无法比对" />
        )}

        <SectionTitle>六、风险项清单</SectionTitle>
        {riskItems.length > 0 ? (
          <Table<RiskItem>
            size="small"
            rowKey="code"
            pagination={false}
            dataSource={riskItems}
            columns={[
              {
                title: '风险项',
                dataIndex: 'code',
                width: 170,
                render: (v: string) => RISK_ITEM_LABELS[v as keyof typeof RISK_ITEM_LABELS] ?? v,
              },
              {
                title: '等级',
                dataIndex: 'level',
                width: 100,
                render: (v: RiskLevel) => <RiskTag level={v} withHint={false} size="small" />,
              },
              { title: '说明与处置建议', dataIndex: 'message' },
            ]}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="未发现风险项" />
        )}

        {showOverrideSection ? (
          <>
            <SectionTitle>七、通道来源与外部校验不可用说明</SectionTitle>
            <Alert
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              message="本报告中的部分校验项未通过真实外部接口完成"
              description={
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  {/* 沙箱/未启用排在前面：它们状态显示「成功」，比"失败"更容易被忽略 */}
                  {untrustedSteps.length > 0 ? (
                    <>
                      <span>
                        <strong>以下校验项显示为「成功」，但结论并非来自真实外部通道</strong>
                        ，不可作为税务凭据或合规依据：
                      </span>
                      <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                        {untrustedSteps.map((step) => (
                          <li key={step.step}>
                            {STEP_LABELS[step.step]} ——{' '}
                            {step.mode ? PROVIDER_MODE_LABELS[step.mode] : '通道未记录'}
                            {step.impl ? `（${step.impl}）` : ''}
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : null}
                  {unavailableSteps.length > 0 ? (
                    <>
                      <span>未完成的校验项及其原因：</span>
                      <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                        {unavailableSteps.map((step) => (
                          <li key={step.step}>
                            {STEP_LABELS[step.step]} —— {CHECK_STATUS_LABELS[step.status]}
                            {step.message ? `：${step.message}` : ''}
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : null}
                  {manualOverride ? (
                    <>
                      <span>
                        接管人：{manualOverride.operatorName}（{manualOverride.operatorId}）· 时间{' '}
                        {manualOverride.at.replace('T', ' ').slice(0, 19)}
                      </span>
                      <span>已人工核验项：{manualOverride.items.join('、')}</span>
                      <span>核验说明：{manualOverride.reason}</span>
                    </>
                  ) : (
                    <span>
                      尚无人工接管记录，本报告对应的校验项仍处于「外部校验暂不可用」状态；
                      该发票需由具备权限的人员在发票详情页执行「人工核验并接管」并填写核验说明后，才能继续流转。
                    </span>
                  )}
                  {detail.error ? (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      注：本次未能取得人工接管记录（{detail.error.message}），请以发票详情页的接管记录为准。
                    </Typography.Text>
                  ) : null}
                </Space>
              }
            />
          </>
        ) : null}

        <SectionTitle>{showOverrideSection ? '八' : '七'}、校验结论与签署</SectionTitle>
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label="综合风险等级">
            <RiskTag level={data.riskLevel} />
          </Descriptions.Item>
          <Descriptions.Item label="是否阻断流转 T+">
            {riskItems.some((i) => i.level === RiskLevel.HIGH) ? (
              <Tag color="red">已阻断（需特例放行）</Tag>
            ) : (
              <Tag color="green">未阻断</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="报告生成时间">
            {new Date().toLocaleString('zh-CN')}（系统自动生成）
          </Descriptions.Item>
          <Descriptions.Item label="当前查看人">
            {auth?.name ?? '-'}（{auth?.username ?? '-'}）
          </Descriptions.Item>
        </Descriptions>

        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message="本报告不构成入账结论"
          description="平台只做风险标记与事实记录，是否入账、以什么科目入账由财务人员在 T+ 端判断并生成凭证。本报告可直接作为钉钉审批或事后抽查的附件使用。"
        />

        <div style={{ marginTop: 24, display: 'flex', gap: 48, flexWrap: 'wrap' }}>
          {['经办人签字', '财务初审签字', '复核人签字'].map((label) => (
            <div key={label} style={{ minWidth: 200 }}>
              <Typography.Text>{label}：</Typography.Text>
              <div style={{ borderBottom: '1px solid #000', height: 36 }} />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                日期：____年__月__日
              </Typography.Text>
            </div>
          ))}
        </div>

        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 16, marginBottom: 0 }}>
          权限说明：本报告中税号、银行账号等敏感字段按查看人权限脱敏展示（当前账号持有{' '}
          {PERMISSION_BY_CODE[maskPermission]?.name ?? maskPermission}）。脱敏不影响报告作为凭据的效力，
          如需完整信息请由具备权限的人员导出。
        </Typography.Paragraph>
      </Card>
    </div>
  );
}
